// web/src/components/Modal.tsx — portal-rendered, focus-trapped, blurred-overlay
// dialog shell. The keystone of modals-over-inline (RUBRIC R7.2) and keyboard
// accessibility (R7.3). All visual values are tokens. OWNED BY S4.
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Icon } from './ui/Icon';

export interface ModalProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
  zIndex?: number;
  /** When false: no close button, Escape + overlay-click disabled (busy flow). */
  dismissable?: boolean;
  onClose: () => void;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  title,
  subtitle,
  children,
  footer,
  maxWidth = 540,
  zIndex = 2000,
  dismissable = true,
  onClose,
}: ModalProps): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const restoreRef = useRef<HTMLElement | null>(null);

  // Escape to close + focus trap; restore focus to the trigger on unmount.
  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;

    // Move initial focus to the first focusable control (or the panel).
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && dismissable) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = nodes[0];
      const lastEl = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restoreRef.current?.focus?.();
    };
  }, [dismissable, onClose]);

  return createPortal(
    <div
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        background: 'var(--color-overlay-bg)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="modal-panel"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--color-bg2)',
          border: '1px solid var(--color-border-modal)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          animation: 'modalIn var(--dur-base) var(--ease-out) both',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '1.1rem 1.25rem 0.75rem',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div>
            {subtitle && (
              <div className="kicker" style={{ marginBottom: '0.3rem' }}>
                {subtitle}
              </div>
            )}
            <h2 id={titleId} style={{ fontSize: 'var(--size-h2)', color: 'var(--color-text)' }}>
              {title}
            </h2>
          </div>
          {dismissable && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="btn btn-ghost btn-sm"
              style={{ flex: '0 0 auto' }}
            >
              <Icon icon={X} size="sm" />
            </button>
          )}
        </header>

        <div style={{ padding: '1.25rem', overflowY: 'auto' }}>{children}</div>

        {footer && (
          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.6rem',
              padding: '0.9rem 1.25rem',
              borderTop: '1px solid var(--color-border)',
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
